import * as React from "react";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../../Shared/Process/Process/ProcessComponent";
import { SectionHeader, TrueFalseRadio } from "../../SharedComponents";
import { RadiosEditor } from "../../../../../../../../react-core/Html/RadiosEditor";
import { EWizardLinterStatus, WizardLinter } from "../../../../../../../Shared/Process/Process/WizardLinter";
import { EWizardLinterHelper } from "../../../Resource/EWizardLinterHelper";
import { PensionTypesArray, PensionTypesMap } from "../../../../Common/Resource/Contents";
import { SubAccountType } from "../../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { incomePaymentDetailsLabels } from "../../../../Common/SIPP/IncomePaymentDetails/Resource/Content";
import { PensionType } from "../../../../../../../../Wrap/Constants/Enums/Domain/PensionType";
import { AlertType } from "../../../../../../../Shared/Entities/Constants/AlertConstants";
import { Alert } from "../../../../../../../Shared/Components/Alert";
import { StaticUrls } from "../../../../../../Resoures/StaticUrls";
import { getQueryParameters } from "../../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { UserClaim } from "../../../../../../../../Wrap/Constants/Enums/UserClaim";
import { checkPermission } from "../../../../../../../Shared/Permission/PermissionHelper";
import { RetirementAge } from "../../../../Common/PaymentsIn/SIPP/RetirementAge";
import { MoneyPurchaseAnnualAllowanceComponent } from "../../../../Common/PaymentsIn/SIPP/MoneyPurchaseAnnualAllowanceComponent";
import { PensionProtection } from "../../../../Common/PaymentsIn/SIPP/PensionProtection";
import { PensionAnnuityAssumption } from "../../../../Common/PaymentsIn/SIPP/PensionAnnuityAssumption";
import { CommonHelper } from "../../../../../../../../react-core/Helpers/CommonHelper";
import { ProcessType } from "../../../../../../../Shared/IST/Resource/Content";
import { DPCQuestion } from "../../../../Common/Pension/DPCQuestion";

interface ISIPPSectionProps extends IProcessComponentProps {
    HasExistingSIPP?: boolean;
    subAccountId?: string;
    subAccountHierarchyId?: string;
    isAddRegularPayment?: boolean;
    onDpcChange: Function;
}

interface ISIPPSectionState { }

export class SIPPSection extends ProcessComponent<ISIPPSectionProps, ISIPPSectionState> {
    constructor(props: any) {
        super(props);
        this.state = {};
    }

    getBlockStatus(): EWizardLinterStatus {
        const { Product: data } = this.props.data;
        const complete =
            data?.PensionType != null &&
            data?.IsIncomePaymentDetailsSelected != null &&
            data?.IsClientOptOutPensionSelected != null;
        return EWizardLinterHelper.getEWizardLinterStatus(complete);
    }

    private onProductChange(changes, product, index) {
        for (const key in changes) {
            product[key] = changes[key];
        }
        const data = this.props.data.SelectedProducts.concat();
        data[index] = product;
        this.props.onChange({ SelectedProducts: data });
    }

    private getProps(key: string) {
        const { onChange, data, showErrorMsgPopUp, isReview } = this.props;

        return {
            type: key,
            data: data[key],
            isReview: isReview,
            showErrorMsgPopUp: (msg, isShow) => showErrorMsgPopUp(msg, isShow),
            onRef: (ref) => this.appendChild(ref),
            onChange: (changes) => this.onDataChange(onChange, changes, data, key),
        };
    }

    public render(): JSX.Element {
        const { data } = this.props;

        const processType = CommonHelper.isNullOrEmpty(getQueryParameters()["hierarchyid"])
            ? ProcessType.NB
            : ProcessType.NBEC;

        const productIndex = data.SelectedProducts.findIndex(
            (e) => e.SubAccountType == SubAccountType.Npr || e.SubAccountType == SubAccountType.Sipp
        );
        const Product = data.SelectedProducts[productIndex];
        return (
            <WizardLinter
                status={this.getBlockStatus()}
                enabled={EWizardLinterHelper.shouldDisplay(SubAccountType.Npr)}
            >
                {SectionHeader("Pension options")}
                {this.props.HasExistingSIPP == true &&
                    checkPermission(UserClaim.AdviserPortalSPTAndRegularPaymentReadWrite) && (
                        <Alert
                            alertType={AlertType.Warning}
                            message={
                                <span>
                                    As your client already has an existing SIPP, you will only be able to add transfers
                                    in for one or more post-retirement SIPP accounts. Please use the{" "}
                                    <a
                                        href={StaticUrls.SinglePaymentUrl(
                                            "20",
                                            this.props.subAccountId,
                                            getQueryParameters()["hierarchyid"],
                                            this.props.subAccountHierarchyId
                                        )}
                                    >
                                        Single Payment/Transfer
                                    </a>{" "}
                                    or{" "}
                                    <a
                                        href={StaticUrls.RegularPaymentUrl(
                                            "20",
                                            this.props.subAccountId,
                                            getQueryParameters()["hierarchyid"],
                                            this.props.subAccountHierarchyId,
                                            this.props.isAddRegularPayment == true ? "add" : "edit"
                                        )}
                                    >
                                        Regular Payments
                                    </a>{" "}
                                    process if you want to add more contributions to an existing SIPP.
                                </span>
                            }
                        />
                    )}
                <RadiosEditor
                    cssWrapper="radio-group rg-150 mb-23"
                    selected={Product.PensionType ?? PensionType.None}
                    label={incomePaymentDetailsLabels.clientPensionBenifits}
                    options={PensionTypesArray}
                    getText={(v) => {
                        return PensionTypesMap[v];
                    }}
                    disabled={this.props.HasExistingSIPP || Product.IsDisqualifyingPensionCredits}
                    onChange={(v) => {
                        this.onProductChange({ PensionType: v }, Product, productIndex);
                    }}
                    required="Please choose SIPP type"
                />
                {TrueFalseRadio(
                    incomePaymentDetailsLabels.needToProvideAnIncome,
                    "IsIncomePaymentDetailsSelected",
                    Product,
                    (key, value) => {
                        this.onProductChange({ [key]: value }, Product, productIndex);
                    },
                    true,
                    {
                        disabled: Product.IsDisqualifyingPensionCredits,
                        cssWrapper: "radio-group rg-150 mb-16",
                    }
                )}
                {TrueFalseRadio(
                    "Has the client opted out, or declined to join, a pension where their employer will contribute?",
                    "IsClientOptOutPensionSelected",
                    Product,
                    (key, value) => {
                        this.onProductChange({ [key]: value }, Product, productIndex);
                    },
                    true,
                    { cssWrapper: "radio-group rg-150 mb-16 has-tooltip" },
                    "The Aberdeen SIPP is suitable for members of employer sponsored schemes and employer payments can be accepted. However, it may not be a suitable option if your client's workplace arrangement offers preferential terms."
                )}

                {data?.SelectedProducts.some((item) => item.SubAccountType == SubAccountType.Npr) && (
                    <>
                        <DPCQuestion
                            data={data}
                            onChange={(changes) => this.props.onDpcChange(changes)}
                            onRef={(ref) => this.appendChild(ref)}
                        />
                    </>
                )}

                <RetirementAge {...this.getProps("AnnuityAssumptions")} clientInfo={data.ClientDetails} />
                <p className="mb-24">This age/date is used for illustration purposes.</p>

                {(this.props.HasExistingSIPP != true || data.AdviceGiven == true) && (
                    <MoneyPurchaseAnnualAllowanceComponent {...this.getProps("PensionApply")} />
                )}
                <PensionProtection {...this.getProps("PensionApply")} label={"Edit pension protection"} isNewInstruction={true}/>
                <PensionAnnuityAssumption processType={processType} {...this.getProps("PensionApply")} />
                <hr className="mb-20 mt-0" />
            </WizardLinter>
        );
    }
}
